import React, { useMemo, useState, useRef } from "react";
import { Tabs, Button, Form, Table, Modal, message, Spin, Empty } from "antd";
import { DeleteOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import DynamicForm from "@/components/DynamicForm";
import AssessmentTable from "@/components/AssessmentTable";
import type { FormInstance } from "antd";
import styles from "../../config/style.module.less";
import editStyles from "./edit.style.module.less";
import { EXCELLENT_REVIEW_TABLE_DATA } from "./columns";
import {
  useRatingData,
  type FlatRatingRecord,
} from "../../hooks/useRatingData";
import { getEvaluationInvitation } from "@/apis/supplier";
import {
  TAB_ITEMS_CONFIG,
  EVALUATION_CATEGORIES,
  ASSESSOR_OPTIONS,
  RATING_TYPE_CONFIG,
  MOCK_ASSESSMENT_INVITE_DATA,
} from "./constants";
import {
  renderAssessmentStatusBadge,
  getRatingDataTableColumns,
  renderPhaseHeader,
  renderParseErrorPlaceholder,
  renderEmptyPlaceholder,
} from "./renders";
import type {
  EvaluationInvitationData,
  PhaseEvaluationDisplayProps,
  AssessmentTableContainerProps,
  AssessmentItemConfig,
  RatingDataTableProps,
  TabsFieldProps,
  TreeNode,
} from "./types";
import {
  useAssessmentDataConverter,
  parseEvaluationData,
} from "../../hooks/useAssessmentDataConverter";

// ==================== 辅助函数 ====================

/**
 * 从树形数据中提取 remarks 映射
 * @param treeData - 树形数据
 * @returns remarks 映射对象 { itemId: remark }
 */
const extractRemarksFromTreeData = (
  treeData?: TreeNode[],
): Record<string, string> => {
  const remarksMap: Record<string, string> = {};

  if (!treeData || !Array.isArray(treeData)) {
    return remarksMap;
  }

  const traverse = (node: TreeNode) => {
    if (node.nodeType === "ITEM") {
      // 从 levelList 中获取第一个被选中的等级的 remark
      const checkedLevel = node.levelList?.find((level) => level.checked);
      if (checkedLevel?.remark) {
        remarksMap[node.id] = checkedLevel.remark;
      }
    }

    // 递归遍历子节点
    if (node.childList && Array.isArray(node.childList)) {
      node.childList.forEach((child) => traverse(child));
    }
  };

  treeData.forEach((node) => traverse(node));
  return remarksMap;
};

// ==================== Phase 评估显示组件 ====================

/**
 * PhaseEvaluationDisplay 组件
 * 显示单个 Phase 的评估数据
 */
const PhaseEvaluationDisplay: React.FC<PhaseEvaluationDisplayProps> = ({
  phaseId,
  evaluationList,
  phaseName,
}) => {
  return (
    <div className={editStyles.phaseEvaluationDisplayContainer}>
      {evaluationList.map((item) => {
        const treeData = parseEvaluationData(item.evaluationData);
        let parseError: Error | null = null;

        if (!treeData && item.evaluationData) {
          try {
            JSON.parse(item.evaluationData);
          } catch (error) {
            parseError = error as Error;
          }
        } else if (treeData) {
        }

        return (
          <div key={item.id}>
            {phaseName &&
              renderPhaseHeader(
                phaseName,
                item.purchaseUserName,
                item.excellentNum,
                item.middleNum,
                item.poorNum,
              )}
            {treeData ? (
              <AssessmentTableContainerReadOnly backendData={treeData} />
            ) : parseError ? (
              renderParseErrorPlaceholder(parseError)
            ) : (
              renderEmptyPlaceholder()
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * 只读模式 - AssessmentTable 容器
 * 使用 Hook 处理数据转换
 */
const AssessmentTableContainerReadOnly = React.memo(
  ({ backendData }: { backendData?: TreeNode[] }) => {
    const { t } = useTranslation("evaluationTab");
    const { convertedData } = useAssessmentDataConverter(backendData);
    const remarksMap = useMemo(
      () => extractRemarksFromTreeData(backendData),
      [backendData],
    );

    if (!backendData || backendData.length === 0) {
      return (
        <div className={editStyles.emptyDataContainer}>
          <Empty description={t("暂无数据")} />
        </div>
      );
    }

    return (
      <div className={editStyles.assessmentTableReadOnlyContainer}>
        {convertedData.map((group) => (
          <AssessmentTableReadOnlyWrapper
            key={`${group.stageId}-${group.dimensionId}`}
            dimensionName={group.dimensionName}
            assessmentItems={group.assessmentItems}
            ratingMap={group.ratingMap}
            remarksMap={remarksMap}
          />
        ))}
      </div>
    );
  },
);

// ==================== 只读模式评估表格包装器 ====================

/**
 * 包装 AssessmentTable 组件，用于初始化选中的等级状态
 */
const AssessmentTableReadOnlyWrapper: React.FC<{
  dimensionName: string;
  assessmentItems: AssessmentItemConfig[];
  ratingMap: Record<string, string>;
  remarksMap?: Record<string, string>;
}> = ({ dimensionName, assessmentItems, ratingMap, remarksMap = {} }) => {
  const tableRef = useRef<any>(null);

  React.useEffect(() => {
    if (tableRef.current?.initializeWithData) {
      tableRef.current.initializeWithData(ratingMap, remarksMap);
    }
  }, [ratingMap, remarksMap]);

  return (
    <AssessmentTable
      ref={tableRef}
      items={assessmentItems}
      title={dimensionName}
      readOnly={true}
    />
  );
};

// ==================== 评级数据表格组件 ====================

/**
 * 通用评级数据表格组件
 * 支持优、中、差、NA四种评级类型
 */
const RatingDataTable: React.FC<RatingDataTableProps> = ({
  ratingType = "excellent",
  ratingJsonData,
  treeData = EXCELLENT_REVIEW_TABLE_DATA,
}) => {
  const { t } = useTranslation("evaluationTab");
  // 解析评级数据
  const ratingDataFromJson = useRatingData(
    ratingType === "excellent" ? ratingJsonData : undefined,
    ratingType === "middle" ? ratingJsonData : undefined,
    ratingType === "poor" ? ratingJsonData : undefined,
    ratingType === "na" ? ratingJsonData : undefined,
  );

  // 提取对应的展平数据
  const flatDataFromJson = useMemo(() => {
    switch (ratingType) {
      case "excellent":
        return ratingDataFromJson.excellentFlatData;
      case "middle":
        return ratingDataFromJson.middleFlatData;
      case "poor":
        return ratingDataFromJson.poorFlatData;
      case "na":
        return ratingDataFromJson.naFlatData;
      default:
        return [];
    }
  }, [ratingDataFromJson, ratingType]);

  // 优先使用 JSON 数据
  const flatData = useMemo(() => {
    return flatDataFromJson.length > 0 ? flatDataFromJson : [];
  }, [flatDataFromJson]);

  const columns = useMemo(
    () => getRatingDataTableColumns(flatData, ratingType),
    [flatData, ratingType],
  );

  if (flatData.length === 0) {
    return (
      <div className={editStyles.excellentReviewTableContainer}>
        <div className={editStyles.emptyPlaceholderContainer}>
          {t("暂无")} {RATING_TYPE_CONFIG[ratingType].label} {t("数据")}
        </div>
      </div>
    );
  }

  return (
    <div className={editStyles.excellentReviewTableContainer}>
      <Table<FlatRatingRecord>
        columns={columns}
        dataSource={flatData}
        pagination={false}
        bordered
        size="middle"
        rowKey="id"
      />
    </div>
  );
};
const getAssessmentInviteFormItems = (
  onDelete?: (index: number) => void,
  formRef?: React.MutableRefObject<FormInstance | null> | FormInstance,
  t?: (key: string) => string,
) => {
  return [
    {
      name: "srmSupplierAccessEvaluationInvitations",
      col: { span: 24 },
      list: [
        {
          label: t?.("评估阶段") || "评估阶段",
          name: "evaluationTemplateIds",
          col: { span: 9 },
          rules: [
            {
              required: true,
              message: t?.("请选择评估阶段") || "请选择评估阶段",
            },
          ],
          widget: {
            widgetType: "Select",
            placeholder: t?.("请选择") || "请选择",
            options: EVALUATION_CATEGORIES,
            mode: "multiple",
          },
        },
        {
          label: t?.("评估人") || "评估人",
          name: "purchaseUserName",
          col: { span: 9 },
          rules: [
            { required: true, message: t?.("请选择评估人") || "请选择评估人" },
          ],
          widget: {
            widgetType: "Select",
            placeholder: t?.("请选择") || "请选择",
            options: ASSESSOR_OPTIONS,
          },
        },
        {
          name: "statusOrAction",
          col: { span: 2 },
          custom: {
            render: (
              fieldName?: number,
              remove?: (index: number) => void,
              fieldValue?: any,
            ) => {
              // 从表单中获取所有评估邀请项
              let form: FormInstance | null = null;
              if (formRef && "getFieldValue" in formRef) {
                // 直接是 FormInstance
                form = formRef as FormInstance;
              } else if (formRef && "current" in formRef) {
                // 是 ref 对象
                form = (formRef as any).current;
              }

              const allAssessmentItems = form?.getFieldValue(
                "srmSupplierAccessEvaluationInvitations",
              );
              const currentRowData = allAssessmentItems?.[fieldName as number];

              // 检查评估状态（evaluationStatus）
              // 0: 草稿, 1: 部门评估中, 2: 部门评估完成, 3: 审批中, 4: 已驳回, 5: 已通过
              const evaluationStatus = currentRowData?.evaluationStatus;
              const isAssessed =
                evaluationStatus === 2 || evaluationStatus === 5;
              const isNotAssessed =
                evaluationStatus === 1 || evaluationStatus === 3;

              return (
                <div className={styles.actionButtonContainer}>
                  {isAssessed ? (
                    renderAssessmentStatusBadge(true)
                  ) : isNotAssessed ? (
                    renderAssessmentStatusBadge(false)
                  ) : (
                    <Button type="primary" onClick={() => {}}>
                      {t?.("发送邀请") || "发送邀请"}
                    </Button>
                  )}
                </div>
              );
            },
          },
        },
        {
          name: "deleteBtn",
          col: { span: 2 },
          custom: {
            render: (fieldName?: number, remove?: (index: number) => void) => (
              <div className={styles.actionButtonContainer}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (remove && fieldName !== undefined) {
                      remove(fieldName);
                    }
                  }}
                >
                  {t?.("删除") || "删除"}
                </Button>
              </div>
            ),
          },
        },
      ],
    },
  ];
};

// ==================== 评估表格容器 ====================

/**
 * 评估表格容器组件
 * 显示评估结果汇总和详情
 */
const AssessmentTableContainer: React.FC<AssessmentTableContainerProps> = ({
  assessmentSummary,
  formData,
}) => {
  const { t } = useTranslation("evaluationTab");
  const [invitationModalVisible, setInvitationModalVisible] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState<number>(0);
  const [invitationData, setInvitationData] =
    useState<EvaluationInvitationData>({});

  // 使用 useRatingData Hook 解析所有评级数据
  const { excellentFlatData, middleFlatData, poorFlatData, naFlatData } =
    useRatingData(
      formData?.excellentSummary,
      formData?.middleSummary,
      formData?.poorSummary,
      formData?.naSummary,
    );

  // 检查是否有任何有效的评级数据
  const hasExcellentData = excellentFlatData.length > 0;
  const hasMiddleData = middleFlatData.length > 0;
  const hasPoorData = poorFlatData.length > 0;
  const hasNaData = naFlatData.length > 0;
  const hasAnyRatingData =
    hasExcellentData || hasMiddleData || hasPoorData || hasNaData;

  // 从 resultDetailsList 计算总体评估结果
  const totalAssessmentSummary = useMemo(() => {
    if (
      !formData?.resultDetailsList ||
      formData.resultDetailsList.length === 0
    ) {
      return {
        excellentCount: 0,
        middleCount: 0,
        poorCount: 0,
      };
    }

    return formData.resultDetailsList.reduce(
      (acc: any, result: any) => ({
        excellentCount: acc.excellentCount + (result.excellentNum ?? 0),
        middleCount: acc.middleCount + (result.middleNum ?? 0),
        poorCount: acc.poorCount + (result.poorNum ?? 0),
      }),
      { excellentCount: 0, middleCount: 0, poorCount: 0 },
    );
  }, [formData?.resultDetailsList]);

  // ==================== 导航处理函数 ====================
  // 计算结果列表中的phaseIndex
  const getPhaseIndex = (phaseId: string | number | null) => {
    if (phaseId === null) return 0;
    return (formData?.resultDetailsList || []).findIndex(
      (item: any) => item.phaseId?.toString() === phaseId?.toString(),
    );
  };

  // 处理上一步导航
  const handlePrevPhase = () => {
    const currentIndex = getPhaseIndex(selectedPhaseId);
    if (currentIndex > 0) {
      const prevPhaseId =
        formData?.resultDetailsList?.[currentIndex - 1]?.phaseId?.toString();
      if (prevPhaseId) {
        setSelectedPhaseId(prevPhaseId);
        setSelectedPhaseIndex(currentIndex - 1);
      }
    }
  };

  // 处理下一步导航
  const handleNextPhase = () => {
    const currentIndex = getPhaseIndex(selectedPhaseId);
    const resultListLength = formData?.resultDetailsList?.length || 0;
    if (currentIndex < resultListLength - 1) {
      const nextPhaseId =
        formData?.resultDetailsList?.[currentIndex + 1]?.phaseId?.toString();
      if (nextPhaseId) {
        setSelectedPhaseId(nextPhaseId);
        setSelectedPhaseIndex(currentIndex + 1);
      }
    }
  };

  // ==================== 处理"查看"按钮点击 ====================
  const handleViewClick = async (result: any) => {
    try {
      setInvitationLoading(true);

      // 获取选中的 phaseId，转换为字符串用于后续查找
      const selectedPhaseIdStr = result.phaseId?.toString() || "";
      setSelectedPhaseId(selectedPhaseIdStr);

      // 计算当前phaseId在列表中的索引
      const phaseIndex = getPhaseIndex(selectedPhaseIdStr);
      setSelectedPhaseIndex(phaseIndex);

      setInvitationModalVisible(true);

      // 组合phaseIds - 从resultDetailsList遍历，获取所有phaseId并逗号分隔
      const phaseIds = (formData?.resultDetailsList || [])
        .map((item: any) => item.phaseId)
        .filter((id: any) => id !== undefined && id !== null)
        .join(",");

      // 调用接口获取评估邀请信息
      const response = await getEvaluationInvitation(
        formData?.id as string | number,
        phaseIds,
      );

      if (response.data?.success && response.data?.code === 200) {
        // 后端返回的是 { data: { 3: [...], 5: [...] } } 的格式
        const data = response.data?.data || {};

        // 验证选中的 phaseId 是否在返回的数据中
        if (data[selectedPhaseIdStr]) {
          // 解析所有 evaluationData 并整合成对象数组
          const evaluationDataArray = data[selectedPhaseIdStr]
            .map((item, idx) => {
              try {
                const evaluationDataObj = JSON.parse(item.evaluationData);
                return {
                  序号: idx + 1,
                  评估人: item.purchaserName,
                  评估人ID: item.purchaserId,
                  评估模板ID: item.evaluationTemplateId,
                  评估ID: item.id,
                  评估状态:
                    item.evaluationStatus === 1
                      ? "已评估"
                      : item.evaluationStatus === 0
                        ? "未评估"
                        : "未知",
                  evaluationData: evaluationDataObj,
                };
              } catch (parseError) {
                return null;
              }
            })
            .filter((item) => item !== null);

          // 以对象数组形式打印
          console.table(evaluationDataArray);
          setInvitationData(data);
          message.success(t("获取评估数据成功"));
        } else {
          message.error(t("获取的评估数据中不包含该阶段的信息请重试"));
        }
      } else {
        message.warning(response.data?.message || t("获取评估数据失败"));
      }
    } catch (error) {
      message.error(t("获取评估数据失败，请重试"));
    } finally {
      setInvitationLoading(false);
    }
  };

  return (
    <div className={editStyles.assessmentContainer}>
      {/* 总体评分结果 */}
      <div className={editStyles.totalScoreContainer}>
        <span className={editStyles.totalScoreLabel}>{t("总体评估结果")}:</span>
        <span className={`${editStyles.scoreBadge} ${editStyles.bad}`}>
          {t("差")}: {totalAssessmentSummary.poorCount}
        </span>
        <span className={`${editStyles.scoreBadge} ${editStyles.medium}`}>
          {t("中")}: {totalAssessmentSummary.middleCount}
        </span>
        <span className={`${editStyles.scoreBadge} ${editStyles.good}`}>
          {t("优")}: {totalAssessmentSummary.excellentCount}
        </span>
      </div>

      {/* 评估结果明细 */}
      <div className={editStyles.assessmentDetailsContainer}>
        {(formData?.resultDetailsList || []).map((result, index) => (
          <div key={index} className={editStyles.assessmentResultRow}>
            <div className={editStyles.resultNameAndScore}>
              <span className={editStyles.resultName}>
                {result.phaseName}：
              </span>
              <span className={editStyles.resultScoreSummary}>
                【{t("差")}: {result.poorNum ?? 0} {t("中")}:{" "}
                {result.middleNum ?? 0} {t("优")}: {result.excellentNum ?? 0}】
              </span>
            </div>
            <div className={editStyles.evaluatorContainer}>
              <span className={editStyles.evaluatorLabel}>
                {t("评估人")}:{" "}
                {Array.isArray(result.purchaseUserNickNames)
                  ? result.purchaseUserNickNames.join("、")
                  : result.purchaseUserNickNames || ""}
              </span>
            </div>
            <div className={editStyles.actionButtonContainer}>
              <Button
                type="link"
                size="small"
                onClick={() => handleViewClick(result)}
                className={editStyles.viewButton}
              >
                {t("查看")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* 评估邀请弹窗 */}
      <Modal
        title={t("评估详情")}
        open={invitationModalVisible}
        onCancel={() => {
          setInvitationModalVisible(false);
          setSelectedPhaseId(null);
          setSelectedPhaseIndex(0);
          setInvitationData({});
        }}
        footer={null}
        width="1000px"
        centered
        style={{ height: "80vh" }}
        styles={{
          body: {
            maxHeight: "calc(80vh - 110px)",
            overflowY: "auto",
          },
        }}
      >
        {/* 左侧导航按钮 - fixed定位 */}
        {invitationModalVisible && (
          <>
            <div className={editStyles.leftNavigationContainer}>
              <Button
                type="text"
                size="large"
                icon={<LeftOutlined style={{ fontSize: "20px" }} />}
                disabled={selectedPhaseIndex === 0}
                onClick={handlePrevPhase}
                className={`${editStyles.navButton} ${selectedPhaseIndex === 0 ? editStyles.navButtonDisabled : editStyles.navButtonEnabled}`}
                title={t("上一个阶段")}
              />
            </div>

            {/* 右侧导航按钮 - fixed定位 */}
            <div className={editStyles.rightNavigationContainer}>
              <Button
                type="text"
                size="large"
                icon={<RightOutlined style={{ fontSize: "20px" }} />}
                disabled={
                  selectedPhaseIndex ===
                  (formData?.resultDetailsList?.length || 1) - 1
                }
                onClick={handleNextPhase}
                className={`${editStyles.navButton} ${selectedPhaseIndex === (formData?.resultDetailsList?.length || 1) - 1 ? editStyles.navButtonDisabled : editStyles.navButtonEnabled}`}
                title={t("下一个阶段")}
              />
            </div>
          </>
        )}

        <Spin spinning={invitationLoading}>
          <div className={editStyles.modalContentContainer}>
            {selectedPhaseId ? (
              // 优先显示选中 phaseId 的数据
              invitationData[selectedPhaseId] &&
              invitationData[selectedPhaseId].length > 0 ? (
                <>
                  <PhaseEvaluationDisplay
                    phaseId={selectedPhaseId}
                    evaluationList={invitationData[selectedPhaseId]}
                    phaseName={
                      formData?.resultDetailsList?.find(
                        (r: any) => r.phaseId?.toString() === selectedPhaseId,
                      )?.phaseName
                    }
                  />
                </>
              ) : (
                <div className={editStyles.noDataPlaceholder}>
                  <div className={editStyles.noDataText}>
                    {t("暂无该阶段的评估数据")}
                  </div>
                </div>
              )
            ) : (
              <div className={editStyles.noDataPlaceholder}>
                {Object.keys(invitationData).length === 0
                  ? t("暂无评估数据")
                  : t("加载中")}
              </div>
            )}
          </div>
        </Spin>
      </Modal>

      {/* 评级数据展示 - 只显示有数据的评级类型 */}
      {hasAnyRatingData && (
        <div className={editStyles.excellentReviewSection}>
          {hasExcellentData && (
            <div>
              <div>
                <div className={editStyles.excellentReviewTitle}>
                  {t("优评")}
                </div>
              </div>
              <RatingDataTable
                ratingType="excellent"
                ratingJsonData={formData?.excellentSummary}
              />
            </div>
          )}

          {hasMiddleData && (
            <div className={editStyles.sectionWrapper}>
              <div className={editStyles.sectionTitleContainer}>
                <div className={editStyles.midTitle}>{t("中评")}</div>
              </div>
              <RatingDataTable
                ratingType="middle"
                ratingJsonData={formData?.middleSummary}
              />
            </div>
          )}

          {hasPoorData && (
            <div className={editStyles.sectionWrapper}>
              <div className={editStyles.sectionTitleContainer}>
                <div className={editStyles.poorTitle}>{t("差评")}</div>
              </div>
              <RatingDataTable
                ratingType="poor"
                ratingJsonData={formData?.poorSummary}
              />
            </div>
          )}

          {hasNaData && (
            <div className={editStyles.sectionWrapper}>
              <div className={editStyles.sectionTitleContainer}>
                <strong>NA</strong>
              </div>
              <RatingDataTable
                ratingType="na"
                ratingJsonData={formData?.naSummary}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== 评估邀请表单容器 ====================

/**
 * 评估邀请表单容器
 * 注意：嵌套在主表单的 TabsField 中，使用 renderForm={false}
 */
const AssessmentInviteFormContainer: React.FC = () => {
  const { t } = useTranslation("evaluationTab");
  const parentForm = Form.useFormInstance();
  const formRef = React.useRef<FormInstance>(null);

  const handleDeleteRow = (fieldName: number) => {
    const form = parentForm || formRef.current;
    if (form) {
      const srmSupplierAccessEvaluationInvitations = form.getFieldValue(
        "srmSupplierAccessEvaluationInvitations",
      );
      if (
        srmSupplierAccessEvaluationInvitations &&
        Array.isArray(srmSupplierAccessEvaluationInvitations)
      ) {
        const newItems = srmSupplierAccessEvaluationInvitations.filter(
          (_: any, index: number) => index !== fieldName,
        );
        form.setFieldValue("srmSupplierAccessEvaluationInvitations", newItems);
      }
    }
  };

  const formItems = getAssessmentInviteFormItems(
    handleDeleteRow,
    parentForm || formRef,
    t,
  );

  React.useEffect(() => {
    const form = parentForm || formRef.current;
    if (form) {
      form.setFieldsValue({
        srmSupplierAccessEvaluationInvitations: MOCK_ASSESSMENT_INVITE_DATA,
      });
    } else {
    }
  }, [parentForm, t]);

  return (
    <div className={editStyles.assessmentInviteFormContainer}>
      <DynamicForm
        ref={formRef}
        formItem={formItems}
        renderForm={false}
        formConfig={{
          onValuesChange: (changedValues: any, allValues: any) => {},
        }}
      />
    </div>
  );
};

export const TabsField: React.FC<TabsFieldProps> = ({
  assessmentSummary,
  formData,
}) => {
  const { t } = useTranslation("evaluationTab");
  const [activeKey, setActiveKey] = React.useState("admission-assessment");

  const renderTabContent = (key: string) => {
    switch (key) {
      case "admission-assessment":
        return (
          <AssessmentTableContainer
            assessmentSummary={assessmentSummary}
            formData={formData}
          />
        );
      case "assessment-invite":
        return <AssessmentInviteFormContainer />;
      default:
        return null;
    }
  };

  const items = TAB_ITEMS_CONFIG.map((item) => ({
    ...item,
    label: t(item.label),
    children: renderTabContent(item.key),
  }));

  return (
    <div className={editStyles.tabsFieldContainer}>
      <Tabs
        type="card"
        activeKey={activeKey}
        onChange={setActiveKey}
        items={items}
      />
    </div>
  );
};
